import { useEffect, useRef } from "react";
import Navbar from "./Navbar3";
import { CheckCircle, ArrowRight } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRocket, faUsers, faBrain } from "@fortawesome/free-solid-svg-icons";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const AboutPage = () => {
  const keyPoints = [
    "Industry-leading expertise in emerging technologies",
    "Customized solutions tailored to your specific business needs",
    "Comprehensive training and support throughout implementation",
    "Continuous research and development to stay ahead of market trends",
    "Strategic partnerships with global technology leaders",
  ];

  const heroTitleRef = useRef(null);
  const heroSubRef = useRef(null);
  const aboutRef = useRef(null);
  const aboutTextRef = useRef(null);
  const aboutImgRef = useRef(null);
  const cultureRef = useRef(null);
  const cultureCardsRef = useRef(null);
  const cultureImgRef = useRef(null);
  const ctaRef = useRef(null);
  const blob1Ref = useRef(null);
  const blob2Ref = useRef(null);
  const blob3Ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero entrance
      gsap.fromTo(
        heroTitleRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2, ease: "power4.out", delay: 0.2 }
      );
      gsap.fromTo(
        heroSubRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power3.out", delay: 0.4 }
      );

      // About section reveal
      gsap.fromTo(
        aboutTextRef.current,
        { x: -60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: aboutRef.current,
            start: "top 80%",
          },
        }
      );
      gsap.fromTo(
        aboutImgRef.current,
        { x: 60, opacity: 0, scale: 0.95 },
        {
          x: 0,
          opacity: 1,
          scale: 1,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: aboutRef.current,
            start: "top 80%",
          },
        }
      );

      // Culture section reveal
      gsap.fromTo(
        cultureCardsRef.current,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: {
            trigger: cultureRef.current,
            start: "top 80%",
          },
        }
      );
      gsap.fromTo(
        cultureImgRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: cultureRef.current,
            start: "top 80%",
          },
        }
      );

      // CTA reveal
      gsap.fromTo(
        ctaRef.current,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ctaRef.current,
            start: "top 90%",
          },
        }
      );
    });

    // Mouse parallax for background blobs
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      if (blob1Ref.current) blob1Ref.current.style.transform = `translate3d(${x * 30}px, ${y * 30}px, 0)`;
      if (blob2Ref.current) blob2Ref.current.style.transform = `translate3d(${x * -40}px, ${y * 20}px, 0)`;
      if (blob3Ref.current) blob3Ref.current.style.transform = `translate3d(${x * 20}px, ${y * -30}px, 0)`;
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      ctx.revert();
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <main className="bg-slate-950 text-white min-h-screen relative overflow-hidden selection:bg-blue-500/30">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:ital,wght@0,400;0,600;1,400;1,600&display=swap');
        .font-playfair { font-family: 'Playfair Display', serif; }
        .font-inter { font-family: 'Inter', sans-serif; }
        .text-gradient-mask {
          background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.65) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .noise-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E");
        }
      `}</style>

      {/* Noise texture overlay */}
      <div className="fixed inset-0 noise-overlay pointer-events-none z-[1] opacity-30" />

      {/* Background Glow Blobs with mouse parallax */}
      <div ref={blob1Ref} className="fixed -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-600/15 blur-[130px] opacity-60 pointer-events-none z-0 will-change-transform" />
      <div ref={blob2Ref} className="fixed bottom-[-100px] right-[-100px] w-[500px] h-[500px] rounded-full bg-purple-600/15 blur-[110px] opacity-50 pointer-events-none z-0 will-change-transform" />
      <div ref={blob3Ref} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-indigo-600/10 blur-[150px] opacity-40 pointer-events-none z-0 will-change-transform" />

      <Navbar />

      <div className="relative z-10">
        {/* --- Hero Section --- */}
        <section className="text-center pt-28 pb-20 px-4 min-h-[60vh] flex flex-col items-center justify-center relative">
          <h1 
            ref={heroTitleRef}
            className="font-inter text-4xl sm:text-5xl md:text-7xl font-black tracking-tight text-gradient-mask opacity-0"
          >
            About MNR Technologies
          </h1>
          <p 
            ref={heroSubRef}
            className="mt-6 text-lg md:text-xl text-gray-400 max-w-3xl mx-auto font-light leading-relaxed opacity-0"
          >
            We believe innovation isn't just about technology — it's about solving the right problems, anticipating change, and <span className="font-playfair italic text-white/70">empowering people</span>. Our team of experts brings a blend of industry knowledge, technical excellence, and a relentless commitment to quality.
          </p>
        </section>

        {/* Section Divider */}
        <div className="relative py-8 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
          </div>
          <div className="relative z-10 w-2 h-2 rounded-full bg-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.5)] animate-pulse" />
        </div>

        {/* --- "Who We Are" Section --- */}
        <section ref={aboutRef} className="py-24 sm:py-32">
          <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-x-16 gap-y-12 items-center">
            {/* Text Content Column */}
            <div ref={aboutTextRef} className="opacity-0">
              <h2 className="font-inter text-2xl font-bold tracking-tight sm:text-4xl text-gradient-mask">
                Who We Are
              </h2>
              <p className="mt-6 text-lg leading-8 text-gray-400 font-light">
                MNR Technologies is a leading provider of innovative technology solutions, dedicated to helping businesses navigate the complexities of digital transformation. With expertise spanning healthcare, banking, AI, cloud computing, and cybersecurity, we deliver tailored solutions that drive growth and operational excellence.
              </p>
              <p className="mt-4 text-lg leading-8 text-gray-400 font-light">
                Our team of experienced professionals combines technical expertise with deep industry knowledge to develop solutions that address your unique challenges and opportunities. We're committed to staying at the forefront of technological advancement through continuous research and development.
              </p>
              <div className="mt-10 space-y-4">
                {keyPoints.map((point, index) => (
                  <div className="flex items-center gap-x-4 group" key={index}>
                    <div className="w-6 h-6 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors duration-300">
                      <CheckCircle className="h-3.5 w-3.5 text-blue-400" aria-hidden="true" />
                    </div>
                    <span className="text-gray-300 group-hover:text-white transition-colors duration-300">{point}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Image Column */}
            <div ref={aboutImgRef} className="flex justify-center opacity-0">
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-600/30 to-purple-600/30 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500" />
                <img
                  src="/about.png"
                  alt="A modern office with people working on computers"
                  width="600"
                  height="400"
                  loading="eager"
                  decoding="async"
                  className="relative rounded-2xl shadow-2xl shadow-black/40 w-full max-w-lg object-cover border border-white/10"
                  onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/600x400/1e293b/94a3b8?text=About+Image'; }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section Divider */}
        <div className="relative py-8 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
          </div>
          <div className="relative z-10 w-2 h-2 rounded-full bg-purple-500/50 shadow-[0_0_12px_rgba(168,85,247,0.5)] animate-pulse" />
        </div>

        {/* --- Culture Section --- */}
        <div ref={cultureRef} className="flex items-center justify-center p-6 py-24">
          <div className="max-w-7xl w-full flex flex-col lg:flex-row gap-10 lg:gap-20 items-center">
            <section ref={cultureCardsRef} className="flex flex-col max-w-xl space-y-6 opacity-0">
              <h2 className="font-inter text-white text-3xl sm:text-4xl font-bold tracking-tight text-gradient-mask">
                Our Culture
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed max-w-md font-light">
                We are a diverse team with a deep commitment to innovation and collaboration. We believe in a culture of continuous learning, growth mindset, and open communication.
              </p>
              <div className="space-y-5">
                <article className="group relative bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-6 flex items-start gap-5 hover:bg-white/[0.06] hover:border-white/20 transition-all duration-500 shadow-2xl shadow-black/40">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500 -z-10" />
                  <div className="w-12 h-12 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <FontAwesomeIcon icon={faRocket} className="text-blue-400 text-xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-lg mb-1">Innovation First</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      We foster a culture of continuous innovation, encouraging our team to push boundaries.
                    </p>
                  </div>
                </article>

                <article className="group relative bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-6 flex items-start gap-5 hover:bg-white/[0.06] hover:border-white/20 transition-all duration-500 shadow-2xl shadow-black/40">
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500 -z-10" />
                  <div className="w-12 h-12 rounded-xl bg-purple-600/10 border border-purple-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <FontAwesomeIcon icon={faUsers} className="text-purple-400 text-xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-lg mb-1">Collaboration</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Our success is built on strong teamwork and open communication across all levels.
                    </p>
                  </div>
                </article>

                <article className="group relative bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-6 flex items-start gap-5 hover:bg-white/[0.06] hover:border-white/20 transition-all duration-500 shadow-2xl shadow-black/40">
                  <div className="absolute -inset-1 bg-gradient-to-r from-emerald-600/20 to-teal-600/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-500 -z-10" />
                  <div className="w-12 h-12 rounded-xl bg-emerald-600/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <FontAwesomeIcon icon={faBrain} className="text-emerald-400 text-xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-lg mb-1">Growth Mindset</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      We invest in our team's professional development and encourage learning at every step.
                    </p>
                  </div>
                </article>
              </div>
            </section>

            <section ref={cultureImgRef} className="flex-1 flex justify-center items-center opacity-0">
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-purple-600/30 to-blue-600/30 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500" />
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
                  alt="Three people working on laptops"
                  width="800"
                  height="600"
                  loading="lazy"
                  decoding="async"
                  className="relative rounded-2xl shadow-2xl shadow-black/40 h-full object-cover max-h-[600px] border border-white/10"
                />
              </div>
            </section>
          </div>
        </div>

        {/* Section Divider */}
        <div className="relative py-8 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
          </div>
          <div className="relative z-10 w-2 h-2 rounded-full bg-emerald-500/50 shadow-[0_0_12px_rgba(16,185,129,0.5)] animate-pulse" />
        </div>

        {/* --- Call to Action Section --- */}
        <div ref={ctaRef} className="text-center py-24 px-4 opacity-0">
          <h2 className="font-inter text-3xl md:text-5xl font-bold text-gradient-mask tracking-tight">
            Ready to Transform Your Business?
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-400 font-light">
            Contact us today to discuss how our solutions and services can help you achieve your business goals.
          </p>
          <a 
            href="/book" 
            className="mt-10 inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 hover:scale-105 shadow-2xl shadow-blue-900/30 hover:shadow-blue-800/40"
          >
            Get in Touch
            <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </div>
    </main>
  );
};

export default AboutPage;